import { useEffect } from "react";
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_IMAGE_PATH,
  DEFAULT_KEYWORDS,
  DEFAULT_TITLE,
  SITE_NAME,
  getAbsoluteUrl,
} from "../seo.js";

function ensureMeta(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([attribute, value]) => {
    element.setAttribute(attribute, value);
  });
}

function ensureLink(rel) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }

  return element;
}

function normalizeStructuredData(structuredData) {
  if (!structuredData) {
    return "";
  }

  if (Array.isArray(structuredData)) {
    const graphItems = structuredData
      .filter(Boolean)
      .map((item) => {
        if (!item || typeof item !== "object") {
          return item;
        }

        const nextItem = { ...item };
        delete nextItem["@context"];
        return nextItem;
      });

    return JSON.stringify({
      "@context": "https://schema.org",
      "@graph": graphItems,
    });
  }

  return JSON.stringify(structuredData);
}

function Seo({
  description = DEFAULT_DESCRIPTION,
  imagePath = DEFAULT_IMAGE_PATH,
  keywords = DEFAULT_KEYWORDS,
  noindex = false,
  pathname,
  structuredData,
  title = DEFAULT_TITLE,
  type = "website",
}) {
  const structuredDataValue = normalizeStructuredData(structuredData);

  useEffect(() => {
    const resolvedPathname = pathname || window.location.pathname;
    const canonicalUrl = getAbsoluteUrl(resolvedPathname);
    const imageUrl = imagePath.startsWith("http") ? imagePath : getAbsoluteUrl(imagePath);
    const robotsContent = noindex
      ? "noindex,nofollow"
      : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1";

    document.title = title;

    ensureMeta('meta[name="description"]', {
      content: description,
      name: "description",
    });
    ensureMeta('meta[name="keywords"]', {
      content: keywords,
      name: "keywords",
    });
    ensureMeta('meta[name="robots"]', {
      content: robotsContent,
      name: "robots",
    });
    ensureMeta('meta[property="og:site_name"]', {
      content: SITE_NAME,
      property: "og:site_name",
    });
    ensureMeta('meta[property="og:locale"]', {
      content: "en_CA",
      property: "og:locale",
    });
    ensureMeta('meta[property="og:title"]', {
      content: title,
      property: "og:title",
    });
    ensureMeta('meta[property="og:description"]', {
      content: description,
      property: "og:description",
    });
    ensureMeta('meta[property="og:type"]', {
      content: type,
      property: "og:type",
    });
    ensureMeta('meta[property="og:url"]', {
      content: canonicalUrl,
      property: "og:url",
    });
    ensureMeta('meta[property="og:image"]', {
      content: imageUrl,
      property: "og:image",
    });
    ensureMeta('meta[property="og:image:alt"]', {
      content: title,
      property: "og:image:alt",
    });
    ensureMeta('meta[name="twitter:card"]', {
      content: "summary_large_image",
      name: "twitter:card",
    });
    ensureMeta('meta[name="twitter:title"]', {
      content: title,
      name: "twitter:title",
    });
    ensureMeta('meta[name="twitter:description"]', {
      content: description,
      name: "twitter:description",
    });
    ensureMeta('meta[name="twitter:image"]', {
      content: imageUrl,
      name: "twitter:image",
    });

    const canonicalLink = ensureLink("canonical");
    canonicalLink.setAttribute("href", canonicalUrl);

    const existingStructuredData = document.head.querySelector("#seo-structured-data");

    if (structuredDataValue) {
      if (existingStructuredData) {
        existingStructuredData.textContent = structuredDataValue;
      } else {
        const script = document.createElement("script");
        script.id = "seo-structured-data";
        script.type = "application/ld+json";
        script.textContent = structuredDataValue;
        document.head.appendChild(script);
      }
    } else if (existingStructuredData) {
      existingStructuredData.remove();
    }
  }, [description, imagePath, keywords, noindex, pathname, structuredDataValue, title, type]);

  return null;
}

export default Seo;
